import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { Icon } from "../components/ui/Icon";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ToggleSwitch } from "../components/ui/ToggleSwitch";
import { MaintenanceOrderDrawer } from "../components/maintenance/MaintenanceOrderDrawer";
import { FaultReportDrawer } from "../components/maintenance/FaultReportDrawer";
import { MaintenanceOrderCreateDrawer, FaultCreateDrawer, PlanFormDrawer, SparePartTypesDrawer } from "../components/maintenance/MaintenanceDrawers";
import { PurchaseRequestDrawer } from "../components/inventory/StockDashboardDrawers";
import type { OverviewItem } from "../components/inventory/StockOverviewSection";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";
import "./Maintenance.css";

type Tab = "orders" | "faults" | "plans" | "parts";
type OrderStatus = "open" | "in_progress" | "waiting_parts" | "completed" | "cancelled";
type Severity = "low" | "medium" | "high" | "critical";

const ORDER_STATUS_META: Record<OrderStatus, { label: string; cls: string; icon: string }> = {
  open: { label: "Açık", cls: "b-warn", icon: "clock" },
  in_progress: { label: "Devam", cls: "b-info", icon: "play" },
  waiting_parts: { label: "Parça Bekliyor", cls: "b-warn", icon: "boxes" },
  completed: { label: "Tamamlandı", cls: "b-success", icon: "check" },
  cancelled: { label: "İptal", cls: "", icon: "x" },
};
const FAULT_STATUS_META: Record<string, { label: string; cls: string; icon: string }> = {
  open: { label: "Açık", cls: "b-warn", icon: "alertTriangle" },
  resolved: { label: "Çözüldü", cls: "b-success", icon: "check" },
  converted: { label: "Bakımda", cls: "b-info", icon: "wrench" },
  cancelled: { label: "İptal", cls: "", icon: "x" },
};
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const TYPE_LABELS: Record<string, string> = { corrective: "Arıza/Onarım", preventive: "Periyodik" };
const OV_STATUS_META: Record<string, { label: string; cls: string; icon: string }> = {
  tukendi: { label: "Tükendi", cls: "b-danger", icon: "x" },
  kritik: { label: "Kritik", cls: "b-warn", icon: "alertTriangle" },
  normal: { label: "Normal", cls: "b-success", icon: "check" },
  esiksiz: { label: "Eşik yok", cls: "", icon: "boxes" },
};

function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtDate(iso: string | null): string { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; }
function fmtDateTime(iso: string | null): string { return iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—"; }

export function MaintenancePage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const canManage = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("manage:maintenance"));
  const canOrder = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("create:order"));
  // Arıza kaydı zenginleştirme (düzenleme / parça / ek) yetkisi — backend uçları `create:work_order` ister.
  const canEditFault = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).includes("create:work_order"));

  const [tab, setTab] = useState<Tab>("orders");

  // ── Emirler ──
  const [oStatus, setOStatus] = useState<OrderStatus | "">("");
  const [oType, setOType] = useState<"corrective" | "preventive" | "">("");
  const [oQ, setOQ] = useState("");
  const [oPage, setOPage] = useState(1);
  const statsQ = trpc.maintenance.stats.useQuery(undefined, { staleTime: 15_000 });
  const ordersQ = trpc.maintenance.order.list.useQuery({
    status: oStatus || null, type: oType || null, q: oQ.trim() || null, page: oPage, pageSize: 25,
  }, { staleTime: 5_000 });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [convertFault, setConvertFault] = useState<{ id: string; label: string; locationId: string | null; severity: string | null } | null>(null);

  // ── Arızalar ──
  const [fStatus, setFStatus] = useState<string>("");
  const [fPage, setFPage] = useState(1);
  const faultsQ = trpc.faultReport.list.useQuery({ status: (fStatus || null) as never, page: fPage, pageSize: 25 }, { staleTime: 5_000 });
  const [faultCreateOpen, setFaultCreateOpen] = useState(false);
  const [faultDetailId, setFaultDetailId] = useState<string | null>(null);
  const resolveFaultMut = trpc.faultReport.resolve.useMutation({ onSuccess: () => qc.invalidateQueries(), onError: (e) => setPageErr(e.message) });

  // ── Planlar ──
  const plansQ = trpc.maintenance.plan.list.useQuery({ activeOnly: false }, { staleTime: 5_000 });
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [planEdit, setPlanEdit] = useState<{ id: string; title: string; description: string | null; intervalDays: number; nextDueAt: string; assignedToId: string | null } | null>(null);
  const planToggleMut = trpc.maintenance.plan.update.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const createFromPlanMut = trpc.maintenance.order.create.useMutation();
  const [planOrderConfirm, setPlanOrderConfirm] = useState<{ id: string; title: string; locationName: string } | null>(null);
  const [pageErr, setPageErr] = useState<string | null>(null);

  // ── Yedek parçalar ──
  // Hangi stok kart türleri "yedek parça" sayılır → modül ayarından (kullanıcı organize eder).
  // Boş liste → tüm stok kartları listelenir (tek türe dayatma yok).
  const spSettingsQ = trpc.maintenance.settings.get.useQuery(undefined, { staleTime: 30_000 });
  const sparePartTypeIds = useMemo(() => spSettingsQ.data?.sparePartTypeIds ?? [], [spSettingsQ.data]);
  const [pPage, setPPage] = useState(1);
  const [typesDrawerOpen, setTypesDrawerOpen] = useState(false);
  const partsQ = trpc.inventory.stockOverview.useQuery(
    { page: pPage, pageSize: 25, kinds: ["stock"], itemTypeIds: sparePartTypeIds.length ? sparePartTypeIds : undefined },
    { enabled: tab === "parts" && spSettingsQ.isSuccess, staleTime: 10_000 },
  );
  const [poItem, setPoItem] = useState<OverviewItem | null>(null);
  const [poMsg, setPoMsg] = useState<string | null>(null);

  const stats = statsQ.data;
  const orders = ordersQ.data?.rows ?? [];
  const faults = faultsQ.data?.rows ?? [];
  const plans = plansQ.data ?? [];
  const parts = (partsQ.data?.rows ?? []) as unknown as OverviewItem[];

  async function createOrderFromPlan(planId: string) {
    setPageErr(null);
    try {
      const res = await createFromPlanMut.mutateAsync({ type: "preventive", title: plans.find((p) => p.id === planId)?.title ?? "Periyodik bakım", planId });
      await qc.invalidateQueries();
      setPlanOrderConfirm(null);
      setDetailId(res.id);
      setTab("orders");
    } catch (e) { setPageErr((e as Error).message); setPlanOrderConfirm(null); }
  }

  const kpi = (label: string, val: string | number, sub: string, color: string) => (
    <div className="dash-kpi" style={{ ["--kpi-color" as string]: color }}>
      <div className="head"><span className="lbl">{label}</span></div>
      <div className="val">{val}</div>
      <div className="sub">{sub}</div>
    </div>
  );

  const drawers = (
    <>
      <MaintenanceOrderDrawer open={!!detailId} orderId={detailId} canManage={canManage} canOrder={canOrder} onClose={() => setDetailId(null)} />
      <FaultReportDrawer open={!!faultDetailId} faultReportId={faultDetailId} canEdit={canEditFault} onClose={() => setFaultDetailId(null)} />
      <MaintenanceOrderCreateDrawer open={createOpen} fault={convertFault} onClose={() => { setCreateOpen(false); setConvertFault(null); }} onCreated={(id) => setDetailId(id)} />
      <FaultCreateDrawer open={faultCreateOpen} onClose={() => setFaultCreateOpen(false)} />
      <PlanFormDrawer open={planFormOpen} plan={planEdit} onClose={() => { setPlanFormOpen(false); setPlanEdit(null); }} />
      <SparePartTypesDrawer open={typesDrawerOpen} onClose={() => setTypesDrawerOpen(false)} />
      <PurchaseRequestDrawer open={!!poItem} item={poItem} onClose={() => setPoItem(null)} onDone={(msg) => setPoMsg(msg)} />
      <ConfirmDialog
        open={!!planOrderConfirm}
        title="Plandan bakım emri oluştur?"
        message={<><b>{planOrderConfirm?.title}</b> ({planOrderConfirm?.locationName}) için periyodik bakım emri açılacak.</>}
        tone="primary"
        confirmLabel="Emir Oluştur"
        cancelLabel="Vazgeç"
        busy={createFromPlanMut.isPending}
        onConfirm={() => planOrderConfirm && createOrderFromPlan(planOrderConfirm.id)}
        onCancel={() => setPlanOrderConfirm(null)}
      />
    </>
  );

  if (isMobile) {
    const mb = (cls: string) => (cls === "b-warn" ? "b-warning" : cls || "b-neutral");
    const mPager = (page: number, setP: (u: (p: number) => number) => void, total: number) => total > 25 ? (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
        <button type="button" className="btn-md secondary sm" disabled={page <= 1} onClick={() => setP((p) => Math.max(1, p - 1))}>‹ Önceki</button>
        <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page}</span>
        <button type="button" className="btn-md secondary sm" disabled={page * 25 >= total} onClick={() => setP((p) => p + 1)}>Sonraki ›</button>
      </div>
    ) : null;
    const TABS: Array<[Tab, string, number | null]> = [
      ["orders", "Bakım Emirleri", ordersQ.data?.total ?? null],
      ["faults", "Arıza Kayıtları", faultsQ.data?.total ?? null],
      ["plans", "Periyodik", plans.length || null],
      ["parts", "Yedek Parça", null],
    ];
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-md danger-outline" style={{ flex: 1, justifyContent: "center" }} onClick={() => setFaultCreateOpen(true)}><Icon name="alertTriangle" size={14} />Arıza</button>
              <button type="button" className="btn-md primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => { setConvertFault(null); setCreateOpen(true); }}><Icon name="plus" size={14} />Bakım Emri</button>
            </div>
          ) : null}
          <div className="m-chips">
            {TABS.map(([k, lb, ct]) => (
              <button key={k} type="button" className={`m-chip${tab === k ? " act" : ""}`} onClick={() => setTab(k)}>{lb}{ct != null ? ` (${ct})` : ""}</button>
            ))}
          </div>
        </div>

        {pageErr ? <div className="form-banner-err" role="alert">{pageErr}</div> : null}

        <div className="dash-kpi-grid">
          {kpi("AÇIK EMİR", stats ? stats.open : "…", "başlamadı", "var(--warning)")}
          {kpi("DEVAM EDEN", stats ? stats.inProgress : "…", "sahada", "var(--info)")}
          {kpi("PARÇA BEKLEYEN", stats ? stats.waitingParts : "…", "tedarik", "var(--warning)")}
          {kpi("GECİKEN PLAN", stats ? stats.overduePlans : "…", "periyodik", "var(--danger)")}
          {kpi("AÇIK ARIZA", stats ? stats.openFaults : "…", "tüm tesis", "var(--danger)")}
          {kpi("BU AY", stats ? stats.completedThisMonth : "…", "tamamlanan", "var(--success)")}
        </div>

        {/* ── Bakım Emirleri ── */}
        {tab === "orders" ? (
          <>
            <div className="m-search"><Icon name="search" size={15} /><input type="search" placeholder="Kod / başlık…" value={oQ} onChange={(e) => { setOQ(e.target.value); setOPage(1); }} aria-label="Ara" /></div>
            <div className="m-chips">
              {([["", "Tüm durumlar"], ...Object.entries(ORDER_STATUS_META).map(([v, m]) => [v, m.label] as [string, string])] as Array<[string, string]>).map(([v, lb]) => (
                <button key={lb} type="button" className={`m-chip${oStatus === v ? " act" : ""}`} onClick={() => { setOStatus(v as OrderStatus | ""); setOPage(1); }}>{lb}</button>
              ))}
            </div>
            {ordersQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : orders.length === 0 ? <div className="m-empty">Bakım emri yok</div> : (
              <>
                {orders.map((o) => {
                  const m = ORDER_STATUS_META[o.status as OrderStatus];
                  return (
                    <MCard pad key={o.id} onClick={() => setDetailId(o.id)} style={{ cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{o.code}</span>
                        <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                      </div>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--neutral-900)", marginTop: 6 }}>{o.title}</div>
                      {o.faultLabel ? <div style={{ fontSize: 11, color: "var(--danger)" }}>Arıza: {o.faultLabel}</div> : o.planTitle ? <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>Plan: {o.planTitle}</div> : null}
                      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <div style={{ flex: 1 }}><div className="m-ovl">Ekipman</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{o.locationName ?? "—"}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Tip</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{TYPE_LABELS[o.type] ?? o.type}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Öncelik</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{o.priority ? SEV_LABELS[o.priority as Severity] : "—"}</div></div>
                      </div>
                      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)", flexWrap: "wrap" }}>
                        <span><span style={{ color: "var(--neutral-400)" }}>Atanan </span>{o.assignedTo ?? "—"}</span>
                        <span><span style={{ color: "var(--neutral-400)" }}>Tarih </span><span className="mono">{fmtDate(o.plannedDate ?? o.createdAt)}</span></span>
                        {o.partCount > 0 ? <span><span style={{ color: "var(--neutral-400)" }}>Parça </span>{o.partCount}</span> : null}
                      </div>
                    </MCard>
                  );
                })}
                {mPager(oPage, setOPage, ordersQ.data?.total ?? 0)}
              </>
            )}
          </>
        ) : null}

        {/* ── Arıza Kayıtları ── */}
        {tab === "faults" ? (
          <>
            <div className="m-chips">
              {([["", "Tümü"], ["open", "Açık"], ["converted", "Bakımda"], ["resolved", "Çözüldü"], ["cancelled", "İptal"]] as Array<[string, string]>).map(([v, lb]) => (
                <button key={lb} type="button" className={`m-chip${fStatus === v ? " act" : ""}`} onClick={() => { setFStatus(v); setFPage(1); }}>{lb}</button>
              ))}
            </div>
            {faultsQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : faults.length === 0 ? <div className="m-empty">Arıza kaydı yok</div> : (
              <>
                {faults.map((f) => {
                  const m = FAULT_STATUS_META[f.status] ?? FAULT_STATUS_META.open!;
                  const canResolve = f.status === "open" || (f.status === "converted" && (!f.maintenanceOrder || f.maintenanceOrder.status === "completed" || f.maintenanceOrder.status === "cancelled"));
                  const canConvert = f.status === "open" && canManage;
                  return (
                    <MCard pad key={f.id}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }} onClick={() => setFaultDetailId(f.id)}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{f.faultLabel}</span>
                          {f.description ? <span style={{ display: "block", fontSize: 11, color: "var(--neutral-500)" }}>{f.description.slice(0, 60)}</span> : null}
                        </span>
                        <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <div style={{ flex: 1 }}><div className="m-ovl">Yer</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{f.locationName ?? "—"}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Kaynak</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{f.centerName ?? "Tesis Geneli"}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Önem</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{f.severity ? SEV_LABELS[f.severity as Severity] : "—"}</div></div>
                      </div>
                      <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)", alignItems: "center", flexWrap: "wrap" }}>
                        <span><span style={{ color: "var(--neutral-400)" }}>Başlangıç </span><span className="mono">{fmtDateTime(f.startedAt)}</span></span>
                        {f.maintenanceOrder ? <button type="button" style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", color: "var(--primary)", textDecoration: "underline", font: "inherit" }} onClick={() => setDetailId(f.maintenanceOrder!.id)}>{f.maintenanceOrder.code}</button> : null}
                        {f.attachmentCount > 0 ? <span style={{ color: "var(--neutral-400)" }}>Ek {f.attachmentCount}</span> : null}
                      </div>
                      {(canConvert || canResolve) ? (
                        <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                          {canConvert ? <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--warning)" }} aria-label="Bakım emrine dönüştür" onClick={() => { setConvertFault({ id: f.id, label: f.faultLabel, locationId: f.locationId, severity: f.severity }); setCreateOpen(true); }}><Icon name="wrench" size={16} /></button> : null}
                          {canResolve ? <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Çözüldü olarak işaretle" onClick={() => resolveFaultMut.mutate({ id: f.id })} disabled={resolveFaultMut.isPending}><Icon name="check" size={16} /></button> : null}
                        </div>
                      ) : null}
                    </MCard>
                  );
                })}
                {mPager(fPage, setFPage, faultsQ.data?.total ?? 0)}
              </>
            )}
          </>
        ) : null}

        {/* ── Periyodik Bakım ── */}
        {tab === "plans" ? (
          <>
            {canManage ? <button type="button" className="btn-md secondary" style={{ justifyContent: "center" }} onClick={() => { setPlanEdit(null); setPlanFormOpen(true); }}><Icon name="plus" size={14} />Yeni Plan</button> : null}
            {plansQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : plans.length === 0 ? <div className="m-empty">Periyodik bakım planı yok</div> : (
              plans.map((p) => (
                <MCard pad key={p.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{p.title}</span>
                      <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{p.locationName} · {p.locationCode}</span>
                    </span>
                    {p.overdue ? <span className="badge b-danger no-dot"><Icon name="alertTriangle" size={11} />Gecikti</span> : (p.isActive ? <span className="badge b-success no-dot">Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>)}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <div style={{ flex: 1 }}><div className="m-ovl">Periyot</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{p.intervalDays} gün</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Son</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtDate(p.lastDoneAt)}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Sonraki</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2, color: p.overdue ? "var(--danger)" : "var(--neutral-800)" }}>{fmtDate(p.nextDueAt)}</div></div>
                  </div>
                  {p.assignedTo ? <div style={{ fontSize: 11.5, color: "var(--neutral-600)", marginTop: 8 }}><span style={{ color: "var(--neutral-400)" }}>Atanan </span>{p.assignedTo}</div> : null}
                  {canManage ? (
                    <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end", alignItems: "center" }}>
                      <ToggleSwitch checked={p.isActive} onChange={(v) => planToggleMut.mutate({ id: p.id, isActive: v })} ariaLabel={`${p.title} planını ${p.isActive ? "pasifleştir" : "aktifleştir"}`} />
                      <button type="button" className="m-iconbtn m-li-act" aria-label="Planı düzenle" onClick={() => { setPlanEdit({ id: p.id, title: p.title, description: p.description, intervalDays: p.intervalDays, nextDueAt: p.nextDueAt, assignedToId: p.assignedToId }); setPlanFormOpen(true); }}><Icon name="edit" size={16} /></button>
                      {p.openOrder ? (
                        <button type="button" className="m-iconbtn m-li-act" aria-label="Açık emri görüntüle" onClick={() => setDetailId(p.openOrder!.id)}><Icon name="eye" size={16} /></button>
                      ) : (
                        <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="Bakım emri oluştur" onClick={() => setPlanOrderConfirm({ id: p.id, title: p.title, locationName: p.locationName })} disabled={createFromPlanMut.isPending}><Icon name="wrench" size={16} /></button>
                      )}
                    </div>
                  ) : null}
                </MCard>
              ))
            )}
          </>
        ) : null}

        {/* ── Yedek Parçalar ── */}
        {tab === "parts" ? (
          <>
            {canManage ? <button type="button" className="btn-md secondary" style={{ justifyContent: "center" }} onClick={() => setTypesDrawerOpen(true)}><Icon name="settings" size={14} />Türleri Düzenle</button> : null}
            {(partsQ.isLoading || spSettingsQ.isLoading) ? <div className="m-empty">Yükleniyor…</div> : parts.length === 0 ? <div className="m-empty">Yedek parça kartı yok</div> : (
              <>
                {parts.map((p) => {
                  const m = OV_STATUS_META[p.status] ?? OV_STATUS_META.esiksiz!;
                  return (
                    <MCard pad key={p.id}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{p.name}</span>
                          <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{p.code}</span>
                        </span>
                        <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                        <div style={{ flex: 1 }}><div className="m-ovl">Eldeki</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtQty(p.onHand)} {p.uom}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Kritik Eşik</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{p.reorderPoint != null ? fmtQty(p.reorderPoint) : "—"}</div></div>
                        <div style={{ flex: 1 }}><div className="m-ovl">Lot</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{p.lotCount}</div></div>
                      </div>
                      {canOrder ? (
                        <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                          <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--info)" }} aria-label="Satınalma talebi" onClick={() => setPoItem(p)}><Icon name="cart" size={16} /></button>
                        </div>
                      ) : null}
                    </MCard>
                  );
                })}
                {mPager(pPage, setPPage, partsQ.data?.total ?? 0)}
              </>
            )}
          </>
        ) : null}

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Bakım / Onarım</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Bakım / Onarım</h1>
          <p className="sub">Bakım emirleri · arıza kayıtları · periyodik bakım · yedek parça</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {canManage ? (
            <>
              <button type="button" className="btn-md danger-outline" onClick={() => setFaultCreateOpen(true)}>
                <Icon name="alertTriangle" size={14} />Arıza Kaydı
              </button>
              <button type="button" className="btn-md primary" onClick={() => { setConvertFault(null); setCreateOpen(true); }}>
                <Icon name="plus" size={14} />Bakım Emri
              </button>
            </>
          ) : null}
        </div>
      </div>

      {pageErr ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{pageErr}</div> : null}

      {/* KPI şeridi */}
      <div className="dash-kpi-grid mt-kpis" style={{ gridTemplateColumns: "repeat(6, 1fr)" }}>
        {kpi("AÇIK EMİR", stats ? stats.open : "…", "başlamadı", "var(--warning)")}
        {kpi("DEVAM EDEN", stats ? stats.inProgress : "…", "sahada", "var(--info)")}
        {kpi("PARÇA BEKLEYEN", stats ? stats.waitingParts : "…", "tedarik", "var(--warning)")}
        {kpi("GECİKEN PLAN", stats ? stats.overduePlans : "…", "periyodik bakım", "var(--danger)")}
        {kpi("AÇIK ARIZA", stats ? stats.openFaults : "…", "tüm tesis", "var(--danger)")}
        {kpi("BU AY", stats ? stats.completedThisMonth : "…", `tamamlanan · ${stats ? fmtQty(stats.downtimeMinThisMonth) : "…"} dk duruş`, "var(--success)")}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-tabs">
          <button type="button" className={`dt2-tab ${tab === "orders" ? "active" : ""}`} onClick={() => setTab("orders")}>Bakım Emirleri{ordersQ.data ? <span className="ct">{ordersQ.data.total}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "faults" ? "active" : ""}`} onClick={() => setTab("faults")}>Arıza Kayıtları{faultsQ.data ? <span className="ct">{faultsQ.data.total}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "plans" ? "active" : ""}`} onClick={() => setTab("plans")}>Periyodik Bakım{plansQ.data ? <span className="ct">{plans.length}</span> : null}</button>
          <button type="button" className={`dt2-tab ${tab === "parts" ? "active" : ""}`} onClick={() => setTab("parts")}>Yedek Parçalar</button>
        </div>

        {/* ── SEKME: Bakım Emirleri ── */}
        {tab === "orders" ? (
          <>
            <div className="mt-filters">
              <div className="mt-filter" style={{ flex: "2 1 220px" }}>
                <span className="mt-filter-label">Ara</span>
                <input className="form-input" type="search" value={oQ} onChange={(e) => { setOQ(e.target.value); setOPage(1); }} placeholder="Kod / başlık…" />
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Durum</span>
                <select className="form-select" value={oStatus} onChange={(e) => { setOStatus(e.target.value as OrderStatus | ""); setOPage(1); }}>
                  <option value="">Tümü</option>
                  {Object.entries(ORDER_STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
                </select>
              </div>
              <div className="mt-filter">
                <span className="mt-filter-label">Tip</span>
                <select className="form-select" value={oType} onChange={(e) => { setOType(e.target.value as "corrective" | "preventive" | ""); setOPage(1); }}>
                  <option value="">Tümü</option>
                  <option value="corrective">Arıza/Onarım</option>
                  <option value="preventive">Periyodik</option>
                </select>
              </div>
            </div>
            <div className="dt2-scroll">
              {ordersQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : orders.length === 0 ? (
                <div className="empty-state"><p className="ttl">Bakım emri yok</p><p className="sub">"+ Bakım Emri" ile oluşturun ya da bir arızayı dönüştürün.</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th style={{ width: 140 }}>Kod</th><th>Başlık</th><th>Ekipman</th><th style={{ width: 100 }}>Tip</th><th style={{ width: 90 }}>Öncelik</th><th>Atanan</th><th style={{ width: 130 }}>Durum</th><th style={{ width: 100 }}>Tarih</th><th className="num" style={{ width: 70 }}>Parça</th></tr></thead>
                  <tbody>
                    {orders.map((o) => {
                      const m = ORDER_STATUS_META[o.status as OrderStatus];
                      return (
                        <tr key={o.id} onClick={() => setDetailId(o.id)} style={{ cursor: "pointer" }} title="Detayı aç">
                          <td className="cell-id">{o.code}</td>
                          <td>{o.title}{o.faultLabel ? <span className="sec" style={{ fontSize: 10.5, color: "var(--danger)" }}>Arıza: {o.faultLabel}</span> : o.planTitle ? <span className="sec" style={{ fontSize: 10.5 }}>Plan: {o.planTitle}</span> : null}</td>
                          <td>{o.locationName ?? "—"}{o.locationCode ? <span className="sec mono" style={{ fontSize: 10.5 }}>{o.locationCode}</span> : null}</td>
                          <td style={{ fontSize: 12 }}>{TYPE_LABELS[o.type] ?? o.type}</td>
                          <td>{o.priority ? SEV_LABELS[o.priority as Severity] : "—"}</td>
                          <td>{o.assignedTo ?? <span className="mt-muted">—</span>}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{fmtDate(o.plannedDate ?? o.createdAt)}</td>
                          <td className="cell-num">{o.partCount > 0 ? o.partCount : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{ordersQ.data?.total ?? 0} kayıt</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setOPage((p) => Math.max(1, p - 1))} disabled={oPage <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{oPage}</span>
                <button type="button" className="pg" onClick={() => setOPage((p) => p + 1)} disabled={oPage * 25 >= (ordersQ.data?.total ?? 0)} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}

        {/* ── SEKME: Arıza Kayıtları ── */}
        {tab === "faults" ? (
          <>
            <div className="mt-filters">
              <div className="mt-filter">
                <span className="mt-filter-label">Durum</span>
                <select className="form-select" value={fStatus} onChange={(e) => { setFStatus(e.target.value); setFPage(1); }}>
                  <option value="">Tümü</option>
                  <option value="open">Açık</option>
                  <option value="converted">Bakımda</option>
                  <option value="resolved">Çözüldü</option>
                  <option value="cancelled">İptal</option>
                </select>
              </div>
            </div>
            <div className="dt2-scroll">
              {faultsQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : faults.length === 0 ? (
                <div className="empty-state"><p className="ttl">Arıza kaydı yok</p><p className="sub">"Arıza Kaydı" ile tesisin herhangi bir noktası için bildirim açın.</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th>Arıza</th><th>Yer</th><th style={{ width: 120 }}>Kaynak</th><th style={{ width: 90 }}>Önem</th><th style={{ width: 130 }}>Başlangıç</th><th style={{ width: 110 }}>Durum</th><th style={{ width: 110 }}>Bakım Emri</th><th className="num" style={{ width: 50 }}>Ek</th><th style={{ width: 120 }}>İşlemler</th></tr></thead>
                  <tbody>
                    {faults.map((f) => {
                      const m = FAULT_STATUS_META[f.status] ?? FAULT_STATUS_META.open!;
                      return (
                        <tr key={f.id} onClick={() => setFaultDetailId(f.id)} style={{ cursor: "pointer" }} title="Arıza detayını aç (düzenleme · yedek parça · ekler)">
                          <td>{f.faultLabel}{f.description ? <span className="sec" style={{ fontSize: 10.5 }}>{f.description.slice(0, 60)}</span> : null}</td>
                          <td>{f.locationName ?? "—"}</td>
                          <td style={{ fontSize: 11.5 }}>{f.centerName ?? <span className="badge-pill">Tesis Geneli</span>}</td>
                          <td>{f.severity ? SEV_LABELS[f.severity as Severity] : "—"}</td>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{fmtDateTime(f.startedAt)}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          <td className="cell-id" style={{ fontSize: 11 }}>{f.maintenanceOrder ? (
                            <button type="button" style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", color: "var(--primary)", textDecoration: "underline", font: "inherit" }} onClick={(e) => { e.stopPropagation(); setDetailId(f.maintenanceOrder!.id); }} title="Bakım emrini aç">{f.maintenanceOrder.code}</button>
                          ) : "—"}</td>
                          <td className="cell-num">{f.attachmentCount > 0 ? f.attachmentCount : "—"}</td>
                          <td>
                            <div className="row-acts">
                              {f.status === "open" && canManage ? (
                                <button type="button" className="iact warning" aria-label="Bakım emrine dönüştür" title="Bakım emrine dönüştür" onClick={(e) => { e.stopPropagation(); setConvertFault({ id: f.id, label: f.faultLabel, locationId: f.locationId, severity: f.severity }); setCreateOpen(true); }}>
                                  <Icon name="wrench" size={14} />
                                </button>
                              ) : null}
                              {(f.status === "open" || (f.status === "converted" && (!f.maintenanceOrder || f.maintenanceOrder.status === "completed" || f.maintenanceOrder.status === "cancelled"))) ? (
                                <button type="button" className="iact success" aria-label="Çözüldü olarak işaretle" title={f.status === "converted" ? "Bağlı emir kapandı — arızayı çözüldü işaretle" : "Çözüldü olarak işaretle"} onClick={(e) => { e.stopPropagation(); resolveFaultMut.mutate({ id: f.id }); }} disabled={resolveFaultMut.isPending}>
                                  <Icon name="check" size={14} />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{faultsQ.data?.total ?? 0} kayıt</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setFPage((p) => Math.max(1, p - 1))} disabled={fPage <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{fPage}</span>
                <button type="button" className="pg" onClick={() => setFPage((p) => p + 1)} disabled={fPage * 25 >= (faultsQ.data?.total ?? 0)} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}

        {/* ── SEKME: Periyodik Bakım ── */}
        {tab === "plans" ? (
          <>
            <div className="mt-filters">
              <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>Ekipman başına periyodik bakım planları — geciken bakımlar kırmızı vurgulanır.</span>
              <span style={{ flex: 1 }} />
              {canManage ? (
                <button type="button" className="btn-md secondary" onClick={() => { setPlanEdit(null); setPlanFormOpen(true); }}>
                  <Icon name="plus" size={14} />Yeni Plan
                </button>
              ) : null}
            </div>
            <div className="dt2-scroll">
              {plansQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : plans.length === 0 ? (
                <div className="empty-state"><p className="ttl">Periyodik bakım planı yok</p><p className="sub">"Yeni Plan" ile ekipman bazlı bakım periyodu tanımlayın.</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th>Plan</th><th>Ekipman</th><th className="num" style={{ width: 100 }}>Periyot</th><th style={{ width: 110 }}>Son Yapılan</th><th style={{ width: 130 }}>Sonraki</th><th>Atanan</th><th style={{ width: 80 }}>Aktif</th><th style={{ width: 130 }}>İşlemler</th></tr></thead>
                  <tbody>
                    {plans.map((p) => (
                      <tr key={p.id}>
                        <td>{p.title}{p.description ? <span className="sec" style={{ fontSize: 10.5 }}>{p.description.slice(0, 60)}</span> : null}</td>
                        <td>{p.locationName}<span className="sec mono" style={{ fontSize: 10.5 }}>{p.locationCode}</span></td>
                        <td className="cell-num">{p.intervalDays} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>gün</span></td>
                        <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{fmtDate(p.lastDoneAt)}</td>
                        <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
                          {p.overdue ? <span className="badge-pill b-danger"><Icon name="alertTriangle" size={11} />{fmtDate(p.nextDueAt)}</span> : fmtDate(p.nextDueAt)}
                        </td>
                        <td>{p.assignedTo ?? <span className="mt-muted">—</span>}</td>
                        <td>
                          {canManage ? (
                            <ToggleSwitch checked={p.isActive} onChange={(v) => planToggleMut.mutate({ id: p.id, isActive: v })} ariaLabel={`${p.title} planını ${p.isActive ? "pasifleştir" : "aktifleştir"}`} />
                          ) : (
                            <span className={`badge-pill ${p.isActive ? "b-success" : ""}`}>{p.isActive ? "Aktif" : "Pasif"}</span>
                          )}
                        </td>
                        <td>
                          <div className="row-acts">
                            {canManage ? (
                              <>
                                <button type="button" className="iact" aria-label="Planı düzenle" title="Düzenle" onClick={() => { setPlanEdit({ id: p.id, title: p.title, description: p.description, intervalDays: p.intervalDays, nextDueAt: p.nextDueAt, assignedToId: p.assignedToId }); setPlanFormOpen(true); }}>
                                  <Icon name="edit" size={14} />
                                </button>
                                {p.openOrder ? (
                                  <button type="button" className="iact info" aria-label="Açık emri görüntüle" title={`Açık emir: ${p.openOrder.code}`} onClick={() => setDetailId(p.openOrder!.id)}>
                                    <Icon name="eye" size={14} />
                                  </button>
                                ) : (
                                  <button type="button" className="iact success" aria-label="Bakım emri oluştur" title="Bu plandan bakım emri oluştur" onClick={() => setPlanOrderConfirm({ id: p.id, title: p.title, locationName: p.locationName })} disabled={createFromPlanMut.isPending}>
                                    <Icon name="wrench" size={14} />
                                  </button>
                                )}
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : null}

        {/* ── SEKME: Yedek Parçalar ── */}
        {tab === "parts" ? (
          <>
            <div className="mt-filters">
              <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>
                {sparePartTypeIds.length
                  ? <>Yedek parça olarak seçilen kart türlerindeki stok kartları — giriş için <Link to="/inventory" style={{ color: "var(--primary)" }}>Stok / Lotlar → Mal Kabul</Link>.</>
                  : <>Tüm stok kartları listeleniyor — <b>Türleri Düzenle</b> ile yalnız yedek parça türlerini gösterebilirsiniz.</>}
              </span>
              <span style={{ flex: 1 }} />
              {canManage ? (
                <button type="button" className="btn-md secondary" onClick={() => setTypesDrawerOpen(true)} title="Yedek parça listesinde gösterilecek kart türlerini seç">
                  <Icon name="settings" size={14} />Türleri Düzenle
                </button>
              ) : null}
            </div>
            <div className="dt2-scroll">
              {partsQ.isLoading || spSettingsQ.isLoading ? <div className="mt-empty">Yükleniyor…</div> : parts.length === 0 ? (
                <div className="empty-state"><p className="ttl">Yedek parça kartı yok</p><p className="sub">{sparePartTypeIds.length ? "Seçili türlerde stok kartı bulunamadı — \"Türleri Düzenle\" ile türleri değiştirin veya Stok Kartları'ndan kart açın." : "Stok Kartları'ndan kart açın; girişler Mal Kabul ile yapılır."}</p></div>
              ) : (
                <table className="dt2">
                  <thead><tr><th>Parça</th><th className="num" style={{ width: 120 }}>Eldeki</th><th className="num" style={{ width: 110 }}>Kritik Eşik</th><th className="num" style={{ width: 70 }}>Lot</th><th style={{ width: 120 }}>Durum</th><th style={{ width: 90 }}>İşlemler</th></tr></thead>
                  <tbody>
                    {parts.map((p) => {
                      const m = OV_STATUS_META[p.status] ?? OV_STATUS_META.esiksiz!;
                      return (
                        <tr key={p.id}>
                          <td>{p.name}<span className="sec mono" style={{ fontSize: 10.5 }}>{p.code}</span></td>
                          <td className="cell-num">{fmtQty(p.onHand)} <span style={{ fontSize: 10, color: "var(--neutral-400)" }}>{p.uom}</span></td>
                          <td className="cell-num">{p.reorderPoint != null ? fmtQty(p.reorderPoint) : "—"}</td>
                          <td className="cell-num">{p.lotCount}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          <td>
                            <div className="row-acts">
                              {canOrder ? (
                                <button type="button" className="iact info" aria-label="Satınalma talebi" title="Satınalma talebi (taslak PO)" onClick={() => setPoItem(p)}>
                                  <Icon name="cart" size={14} />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot">
              <span>{partsQ.data?.total ?? 0} parça{poMsg ? ` · ${poMsg}` : ""}</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <button type="button" className="pg" onClick={() => setPPage((p) => Math.max(1, p - 1))} disabled={pPage <= 1} aria-label="Önceki">‹</button>
                <span className="mono">{pPage}</span>
                <button type="button" className="pg" onClick={() => setPPage((p) => p + 1)} disabled={pPage * 25 >= (partsQ.data?.total ?? 0)} aria-label="Sonraki">›</button>
              </span>
            </div>
          </>
        ) : null}
      </div>

      {drawers}
    </div>
  );
}
